import { useEffect, useState } from 'react';
import {
  Modal,
  Spinner,
  Alert,
  Badge,
  Table,
  ListGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faTimes,
  faCalendar,
  faClock,
  faMapPin,
  faPoundSign,
  faUserMd
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { getShiftApplicants } from 'features/booking/bookingApi';

interface ShiftApplicantsModalProps {
  show: boolean;
  shiftId: number | null;
  shiftData?: any;
  onHide: () => void;
  onViewApplicant: (bookingId: number) => void;
}

const ShiftApplicantsModal = ({
  show,
  shiftId,
  shiftData,
  onHide,
  onViewApplicant
}: ShiftApplicantsModalProps) => {
  const [applicants, setApplicants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show && shiftId) {
      fetchApplicants();
    }
  }, [show, shiftId]);

  const fetchApplicants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getShiftApplicants(shiftId!);
      setApplicants(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge bg="success">Confirmed</Badge>;
      case 'cancelled':
        return <Badge bg="danger">Cancelled</Badge>;
      case 'completed':
        return <Badge bg="info">Completed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton className="border-bottom border-translucent">
        <Modal.Title className="fs-5 fw-bold">
          <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
          Shift Applicants
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Shift Info Card */}
        {shiftData && (
          <div className="mb-4 p-3 bg-body-secondary rounded-2">
            <div className="row g-2">
              <div className="col-md-6">
                <small className="text-body-tertiary fw-bold">
                  Date & Time
                </small>
                <p className="mb-0 fs-9">
                  <FontAwesomeIcon
                    icon={faCalendar}
                    className="me-1 text-primary"
                  />
                  {new Date(shiftData.date).toLocaleDateString('en-GB')}{' '}
                  <FontAwesomeIcon
                    icon={faClock}
                    className="ms-2 me-1 text-info"
                  />
                  {shiftData.start_time?.slice(0, 5)} -{' '}
                  {shiftData.end_time?.slice(0, 5)}
                </p>
              </div>
              <div className="col-md-6">
                <small className="text-body-tertiary fw-bold">Location</small>
                <p className="mb-0 fs-9">
                  <FontAwesomeIcon
                    icon={faMapPin}
                    className="me-1 text-danger"
                  />
                  {shiftData.location}
                </p>
              </div>
              <div className="col-md-6">
                <small className="text-body-tertiary fw-bold">Pay Rate</small>
                <p className="mb-0 fs-9 fw-semibold">
                  <FontAwesomeIcon
                    icon={faPoundSign}
                    className="me-1 text-success"
                  />
                  £{shiftData.pay_rate}
                </p>
              </div>
              <div className="col-md-6">
                <small className="text-body-tertiary fw-bold">Role</small>
                <p className="mb-0 fs-9">
                  <FontAwesomeIcon
                    icon={faUserMd}
                    className="me-1 text-success"
                  />
                  {shiftData.locum_role.charAt(0).toUpperCase() + shiftData.locum_role.slice(1)}
                </p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" className="text-primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-0">
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            {error}
          </Alert>
        ) : applicants.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-body-secondary mb-0">
              No applicants for this shift yet
            </p>
          </div>
        ) : (
          <div className="table-responsive p-3">
            <Table hover className="phoenix-table mb-0">
              <thead className="border-bottom border-translucent">
                <tr>
                  <th className="fw-bold text-uppercase fs-9 w-25">Name</th>
                  <th className="fw-bold text-uppercase fs-9 w-25">Role</th>
                  <th className="fw-bold text-uppercase fs-9 w-25">Experience</th>
                  <th className="fw-bold text-uppercase fs-9 w-25">Status</th>
                  {/* <th className="fw-bold text-uppercase fs-9 text-end">
                    Action
                  </th> */}
                </tr>
              </thead>
              <tbody>
                {applicants.map(applicant => (
                  <tr key={applicant.booking_id} className="align-middle">
                    <td>
                      <div>
                        <p className="mb-1 fw-semibold text-body-emphasis">
                          {applicant.name}
                        </p>
                        {/* <small className="text-body-secondary">
                          {applicant.email}
                        </small> */}
                      </div>
                    </td>

                    <td>
                      <Badge bg="body-secondary" className="text-body-emphasis">
                        {applicant.locum_role
                          ? applicant.locum_role.charAt(0).toUpperCase() +
                          applicant.locum_role.slice(1)
                          : 'N/A'}
                      </Badge>
                    </td>

                    <td>
                      <span className="text-body-emphasis">
                        {applicant.experience_years || 0} years
                      </span>
                    </td>

                    <td>{getStatusBadge(applicant.status)}</td>

                    {/* <td className="text-end">
                      <Button
                        size="sm"
                        variant="phoenix-primary"
                        onClick={() => onViewApplicant(applicant.booking_id)}
                      >
                        View Details
                      </Button>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ShiftApplicantsModal;
